import {
  call,
  delay,
  select,
  takeLatest,
  put,
  race,
  take,
} from 'redux-saga/effects';
import * as api from '@api/uploadForm';
import { actions, selectSiteUploadState } from './siteUpload.slice';
import { Resp, dispatch } from '../helpers';
import {
  CreateVisitAction,
  SubmitUploadAction,
  UploadFileAction,
  UploadStatus,
  VerificationStepsItem,
} from '@restore/siteUpload/siteUpload.types';
import {
  getPolingStatus,
  getSignedUrl,
  setUploadFile,
  setUploadFinalize,
  updateUploadForm,
} from '@api/uploadForm';
import { CreateVisit } from 'src/store-redux/siteUpload/siteUploadStore.types';
import {
  PreprocessingStatus,
  StatusCodes,
} from '@components/SiteUserUpload/types/uploadTypes';
import { runToast, showErrorToast } from '@restore/tools';
import { selectPaginationAndSort } from '../paginationAndSort/paginationAndSort.slice';
import { fetchSiteSubjectsListWorker } from '@restore/SiteSubjectsList/SiteSubjectsList.saga';
import {
  fetchSiteSubjectsList,
  selectSiteSubjectsListState,
} from '../SiteSubjectsList/SiteSubjectsList.slice';
import { getFileContentType } from '@components/SiteUserUpload/Components/Steps/utils/detectFileExtension';
import { selectClinicalTrialContextState } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';

export function* createVisitWorker({ payload }: CreateVisitAction) {
  try {
    const response: Resp<typeof api.uploadVisit> = yield call(
      api.uploadVisit,
      payload.data,
      payload.selectedClinicalTrial,
      payload.selectedSite
    );
    yield dispatch(
      actions.createVisitSuccess({
        data: {
          ...response.data,
          visitDefinitionId: payload.data.visitDefinitionId,
        },
      })
    );
  } catch (error) {
    yield dispatch(actions.createVisitFailure(error as Error));
  }
}

export function* getEDTFSchemasWorker() {
  try {
    const { data }: ReturnType<typeof selectClinicalTrialContextState> =
      yield select(selectClinicalTrialContextState);
    const uploadData: ReturnType<typeof selectSiteUploadState> = yield select(
      selectSiteUploadState
    );

    if (!data?.configuration.id || !uploadData.fileType || !uploadData.data) {
      return;
    }
    yield dispatch(actions.getEDTFSchemas());
    const response: Resp<typeof api.fetchUploadForm> = yield call(
      api.fetchUploadForm,
      data?.configuration.id ?? '',
      uploadData.fileType,
      uploadData.data.siteId,
      uploadData?.data?.visitDefinitionId
    );
    // Exclude schemas where uiSchema.formType === 'VS' (VS = Validation Specialist form)
    const nonVsSchemas = response.data.filter((schema) => schema.uiSchema?.formType !== 'VS');
    yield dispatch(actions.getEDTFSchemasSuccess(nonVsSchemas));
  } catch (error) {
    yield dispatch(actions.getEDTFSchemasFailure(error as Error));
  }
}

export function* fileStatusPolling() {
  const FILE_PROCESSING_IN_PROGRESS_STATUSES = [
    PreprocessingStatus.CREATED,
    PreprocessingStatus.UPLOADED,
  ];

  const getStepStatus = (
    verificationSteps: VerificationStepsItem[],
    preprocessingStatus: PreprocessingStatus
  ) => {
    if (preprocessingStatus === PreprocessingStatus.PARSING_FAILED) {
      return UploadStatus.FILE_DICOM_ERROR;
    }
    if (verificationSteps.some(step => step.status === 'WARNING')) {
      return UploadStatus.FILE_POLING_WARNING;
    }
    return UploadStatus.FILE_POLING_SUCCESS;
  };

  const {
    data,
  }: ReturnType<typeof selectSiteUploadState> = yield select(
    selectSiteUploadState
  );
  if (!data) {
    throw new Error('no data');
  }
  try {
    while (true) {
      const pollResponse: Resp<typeof api.getPolingStatus> = yield call(
        getPolingStatus,
        data.id,
        data.clinicalTrialId,
        data.siteId
      );
      const pollingInProgress = FILE_PROCESSING_IN_PROGRESS_STATUSES.includes(
        pollResponse?.data?.preprocessingStatus
      );
      if (!pollingInProgress) {
        const status = getStepStatus(
          pollResponse.data.verificationSteps,
          pollResponse.data.preprocessingStatus
        );
        yield dispatch(actions.setStatus(status));
        yield dispatch(actions.getPolingStatusCheck(pollResponse?.data));
      }
      yield delay(5000);

      const { shouldContinuePolling } = yield select(selectSiteUploadState);
      if (!shouldContinuePolling || !pollingInProgress) break;
    }
  } catch (e) {
    yield dispatch(actions.getPolingStatusFailure(e as Error));
  }
}
function* runUploadFlow({ payload }: UploadFileAction) {
  const {
    data,
    selectedSchema,
    edtfData = {},
    signedUrlResponse: existingUrl,
  }: ReturnType<typeof selectSiteUploadState> = yield select(
    selectSiteUploadState
  );

  if (!data) {
    throw new Error('No data');
  }
  if (!selectedSchema || !data.fileType) {
    return;
  }

  yield dispatch(actions.setShouldPolling(true));
  let url = existingUrl?.signedUrl;
  if (!url) {
    yield call(updateUploadForm, {
      data: edtfData as object,
      uploadId: data.id,
      questionnaireId: selectedSchema.id,
      clinicalTrialId: data.clinicalTrialId,
      siteId: data.siteId,
    });
    const signedUrlResponse: Resp<typeof api.getSignedUrl> = yield call(
      getSignedUrl,
      data.id,
      data.clinicalTrialId,
      data.siteId
    );
    url = signedUrlResponse.data.signedUrl;
    yield dispatch(actions.setSignedUrlResponse(signedUrlResponse.data));
  }

  const controller = new AbortController();
  const signal = controller.signal;

  const contentType = getFileContentType({
    fileType: data.fileType,
  }) as string;

  const response: Resp<typeof api.setUploadFile> = yield call(
    setUploadFile,
    payload.file,
    url,
    contentType,
    { signal }
  );

  yield dispatch(
    actions.uploadFileSuccess({
      status:
        response?.code === StatusCodes.OK
          ? UploadStatus.FILE_UPLOAD_SUCCESS
          : UploadStatus.FILE_UPLOAD_ERROR,
      shouldContinuePolling: false,
    })
  );

  yield dispatch(actions.finalizeUpload(null));

  yield call(
    setUploadFinalize,
    data.id,
    data.clinicalTrialId,
    data.siteId,
    payload.additionalComment
  );

  yield call(fileStatusPolling);
}

export function* uploadFileWorker(action: UploadFileAction) {
  try {
    const { cancelled } = yield race({
      upload: call(runUploadFlow, action),
      cancelled: take(actions.cancelUpload.type),
    });

    if (cancelled) {
      return;
    }
  } catch (error) {
    yield showErrorToast(error, 'An error occurred while uploading the file.');
    yield dispatch(actions.finalizeUploadFailure(null));
  }
}

export function* submitUploadWorker({ payload }: SubmitUploadAction) {
  const { data }: ReturnType<typeof selectSiteUploadState> = yield select(
    selectSiteUploadState
  );
  const paginationAndSortData: ReturnType<typeof selectPaginationAndSort> =
    yield select(selectPaginationAndSort);

  const filtersData: ReturnType<typeof selectSiteSubjectsListState> =
    yield select(selectSiteSubjectsListState);
  if (!data) {
    throw new Error('no data');
  }
  try {
    yield call(
      api.submitUpload,
      data.id,
      data.clinicalTrialId,
      data.siteId,
      payload.verificationStepsData
    );
    yield call(fetchSiteSubjectsListWorker, {
      type: fetchSiteSubjectsList.type,
      payload: {
        siteId: data.siteId,
        clinicalTrialId: data.clinicalTrialId,
        args: {
          page: paginationAndSortData?.lists?.subjectList?.currentPage,
          size: paginationAndSortData?.lists?.subjectList?.currentPageSize,
          sort: paginationAndSortData?.lists?.subjectList?.sortBy,
          patientCode: filtersData?.search ?? null,
        },
      },
    });
    yield dispatch(actions.submitUploadSuccess());
    yield call(
      runToast,
      'success',
      `File for ${data.visitName} was successfully uploaded!`
    );
  } catch (error) {
    yield showErrorToast(
      error,
      'An error occurred while submitting uploading the file.'
    );
    yield dispatch(actions.submitUploadFailure(null));
  }
}
function* reUploadWorker() {
  try {
    const { data, fileType }: ReturnType<typeof selectSiteUploadState> =
      yield select(selectSiteUploadState);
    if (data) {
      const {
        patientId,
        visitId,
        clinicalTrialId,
        siteId,
        earlyTerminated,
        createUnscheduled,
      } = data;
      yield call(createVisitWorker, {
        type: actions.createVisit.type,
        payload: {
          data: {
            patientId,
            visitId,
            clinicalTrialId,
            siteId,
            earlyTerminated,
            createUnscheduled,
            fileType,
          } as CreateVisit,
          selectedSite: data.siteId,
          selectedClinicalTrial: data.clinicalTrialId,
        },
      });
    }
  } catch (e) {
    console.error(e);
  }
}

export function* cancelUploadWorker() {
  try {
    const { data } = yield select(selectSiteUploadState);
    if (!data) throw new Error('No upload data');
    yield call(api.cancelUpload, data.id, data.clinicalTrialId, data.siteId);
    yield put(actions.cancelUploadSuccess());
  } catch (error) {
    yield put(actions.cancelUploadFailure(error));
  }
}

export function* siteUploadSaga() {
  yield takeLatest(actions.createVisit.type, createVisitWorker);
  yield takeLatest(actions.createVisitSuccess.type, getEDTFSchemasWorker);
  yield takeLatest(actions.uploadFile.type, uploadFileWorker);
  yield takeLatest(actions.submitUpload.type, submitUploadWorker);
  yield takeLatest(actions.setReupload.type, reUploadWorker);
  yield takeLatest(actions.cancelUpload.type, cancelUploadWorker);
}
